import { useEffect, useState } from "react";
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  InlineGrid,
  InlineStack,
  Link,
  Spinner,
  Text,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { formatFriendlyDate, numberWithCommas } from "../../utility/funcs";
import FieldLinkShare, { unitLabel } from "./FieldLinkShare";

// What the provider captured from their phone for an origin='field' service,
// plus the checks staff should look at before attaching the CFDI: does the
// photographed plate match the car, and does the typed odometer match both
// the photo and the GPS.

const KIND_LABELS = { plate: "Placa", odometer: "Odómetro", other: "Otras" };
// Beyond this the typed km and the photo/GPS disagree enough to look at.
const ODO_TOLERANCE_KM = 500;

function FieldCaptureCard({ service, canEdit }) {
  const [link, setLink] = useState(undefined);
  const [cats, setCats] = useState({});
  const [urls, setUrls] = useState({});
  const [regenerating, setRegenerating] = useState(false);
  // The /odometer_requests entry opened when the km disagreed with the system.
  const [odoRequest, setOdoRequest] = useState(null);

  const load = async () => {
    const { data } = await supabase
      .from("service_capture_links")
      .select("*")
      .eq("service_id", service.id)
      .is("revoked_at", null)
      .maybeSingle();
    setLink(data || null);

    const { data: req } = await supabase
      .from("car_odometer_submissions")
      .select("id, status")
      .eq("service_id", service.id)
      .maybeSingle();
    setOdoRequest(req || null);

    const photos = data?.photos || [];
    if (photos.length) {
      const { data: signed } = await supabase.storage
        .from("services-evidence")
        .createSignedUrls(photos.map((p) => p.path), 3600);
      setUrls(Object.fromEntries((signed || []).map((s) => [s.path, s.signedUrl])));
    }
  };

  useEffect(() => {
    load();
    supabase
      .from("field_service_categories")
      .select("key, label, sort")
      .order("sort")
      .then(({ data }) =>
        setCats(Object.fromEntries((data || []).map((c) => [c.key, c]))),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service.id]);

  const regenerate = async () => {
    setRegenerating(true);
    const { error } = await supabase.rpc("regenerate_field_service_link", {
      p_service: service.id,
    });
    setRegenerating(false);
    if (error) alert(error.message);
    else load();
  };

  if (link === undefined)
    return (
      <Card>
        <Spinner size="small" />
      </Card>
    );

  if (!link)
    return (
      <Card>
        <BlockStack gap="200">
          <Text variant="headingMd">Captura del proveedor</Text>
          <Text tone="subdued">Este servicio no tiene una liga activa.</Text>
          {canEdit && (
            <InlineStack>
              <Button loading={regenerating} onClick={regenerate}>
                Generar liga
              </Button>
            </InlineStack>
          )}
        </BlockStack>
      </Card>
    );

  const expired = new Date(link.expires_at) < new Date();
  const awaiting = service.status === "awaiting_provider";
  const photos = link.photos || [];
  const plate = service.car?.plate || service.sublease_plate;

  const odoDiffAi =
    link.odometer_entered && link.odometer_ai
      ? link.odometer_entered - link.odometer_ai
      : null;
  const odoDiffGps =
    link.odometer_entered && link.odometer_system
      ? link.odometer_entered - link.odometer_system
      : null;

  // Group evidence by category, then everything else by kind.
  const groups = [];
  for (const kind of ["plate", "odometer"]) {
    const ps = photos.filter((p) => p.kind === kind);
    if (ps.length) groups.push({ title: KIND_LABELS[kind], photos: ps });
  }
  const evidenceCats = [
    ...new Set(photos.filter((p) => p.kind === "evidence").map((p) => p.category)),
  ];
  for (const k of evidenceCats)
    groups.push({
      title: cats[k]?.label || "Evidencia",
      note: link.category_notes?.[k],
      photos: photos.filter((p) => p.kind === "evidence" && p.category === k),
    });
  const others = photos.filter((p) => p.kind === "other");
  if (others.length) groups.push({ title: KIND_LABELS.other, photos: others });

  const catLabel = (k) => cats[k]?.label || k;
  const expected = link.expected_categories || [];
  const reported = link.done_categories || [];
  // Requested by staff but not reported by the provider: worth a call.
  const notReported = link.submitted_at ? expected.filter((k) => !reported.includes(k)) : [];

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingMd">Captura del proveedor</Text>
          <Text tone="subdued" variant="bodySm">
            {link.submitted_at
              ? `Enviado ${formatFriendlyDate(link.submitted_at)}`
              : link.opened_at
                ? `Abierta ${formatFriendlyDate(link.opened_at)} · sin enviar`
                : "El proveedor aún no abre la liga"}
          </Text>
        </InlineStack>

        {service.location_lat != null && (
          <Text>
            Realizado en:{" "}
            <Link
              url={`https://www.google.com/maps?q=${service.location_lat},${service.location_lng}`}
              target="_blank"
            >
              {[service.location_colonia, service.location_municipio, service.location_estado]
                .filter(Boolean)
                .join(", ") || "Ver en mapa"}
            </Link>
          </Text>
        )}

        {awaiting && (
          <BlockStack gap="200">
            {expired ? (
              <Banner tone="warning">
                La liga expiró el {formatFriendlyDate(link.expires_at)}. Genera
                una nueva para que el proveedor pueda capturar.
              </Banner>
            ) : (
              <FieldLinkShare
                code={link.code}
                unit={unitLabel({
                  modelName: service.car_model?.name,
                  plate,
                  year: service.car?.year,
                })}
              />
            )}
            {canEdit && (
              <InlineStack>
                <Button variant="plain" loading={regenerating} onClick={regenerate}>
                  Generar nueva liga (invalida la anterior)
                </Button>
              </InlineStack>
            )}
          </BlockStack>
        )}

        {link.instructions && (
          <BlockStack gap="100">
            <Text fontWeight="semibold">Indicaciones enviadas</Text>
            <Text tone="subdued">{link.instructions}</Text>
          </BlockStack>
        )}

        {(link.plate_ai || link.odometer_entered || link.odometer_ai) && (
          <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
            <BlockStack gap="100">
              <Text fontWeight="semibold">Placa (leída por IA)</Text>
              {link.plate_ai ? (
                <InlineStack gap="200" blockAlign="center">
                  <Text>{link.plate_ai}</Text>
                  {link.plate_matches ? (
                    <Badge tone="success">Coincide</Badge>
                  ) : (
                    <Badge tone="critical">{`No coincide con ${plate}`}</Badge>
                  )}
                </InlineStack>
              ) : (
                <Text tone="subdued">No legible</Text>
              )}
            </BlockStack>
            <BlockStack gap="100">
              <Text fontWeight="semibold">Odómetro</Text>
              <Text>
                Capturado:{" "}
                {link.odometer_entered ? `${numberWithCommas(link.odometer_entered)} km` : "—"}
              </Text>
              <InlineStack gap="200" blockAlign="center">
                <Text tone="subdued">
                  Foto (IA): {link.odometer_ai ? `${numberWithCommas(link.odometer_ai)} km` : "—"}
                </Text>
                {odoDiffAi != null && Math.abs(odoDiffAi) > ODO_TOLERANCE_KM && (
                  <Badge tone="critical">{`Difiere ${numberWithCommas(odoDiffAi)} km`}</Badge>
                )}
              </InlineStack>
              <InlineStack gap="200" blockAlign="center">
                <Text tone="subdued">
                  GPS al enviar:{" "}
                  {link.odometer_system ? `${numberWithCommas(link.odometer_system)} km` : "—"}
                </Text>
                {odoDiffGps != null && Math.abs(odoDiffGps) > ODO_TOLERANCE_KM && (
                  <Badge tone="warning">{`Difiere ${numberWithCommas(odoDiffGps)} km`}</Badge>
                )}
                {odoRequest?.status === "pending" && (
                  <Link url="/odometer_requests">Ajustar odómetro</Link>
                )}
                {odoRequest?.status === "approved" && <Badge tone="success">Odómetro ajustado</Badge>}
              </InlineStack>
            </BlockStack>
          </InlineGrid>
        )}

        {(reported.length > 0 || expected.length > 0) && (
          <BlockStack gap="100">
            <Text fontWeight="semibold">
              {link.submitted_at ? "Servicios reportados" : "Servicios solicitados"}
            </Text>
            <InlineStack gap="100">
              {(link.submitted_at ? reported : expected).map((k) => (
                <Badge key={k} tone={link.submitted_at ? "info" : undefined}>
                  {catLabel(k)}
                </Badge>
              ))}
              {notReported.map((k) => (
                <Badge key={k} tone="warning">{`Solicitado, no reportado: ${catLabel(k)}`}</Badge>
              ))}
            </InlineStack>
          </BlockStack>
        )}

        {link.provider_notes && (
          <BlockStack gap="100">
            <Text fontWeight="semibold">Comentarios del proveedor</Text>
            <Text>{link.provider_notes}</Text>
          </BlockStack>
        )}

        {groups.map((g) => (
          <BlockStack gap="100" key={g.title}>
            <Text fontWeight="semibold">{g.title}</Text>
            {g.note && <Text tone="subdued">{g.note}</Text>}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {g.photos.map((p) => (
                <div key={p.path} style={{ textAlign: "center", width: 110 }}>
                    <a href={urls[p.path]} target="_blank" rel="noreferrer" title={p.label || ""}>
                      <img
                        src={urls[p.path]}
                        alt={p.label || g.title}
                        style={{ width: 110, height: 110, objectFit: "cover", borderRadius: 8, background: "#eee" }}
                      />
                    </a>
                    {p.label && (
                      <Text variant="bodyXs" tone="subdued">
                        {p.label}
                      </Text>
                    )}
                </div>
              ))}
            </div>
          </BlockStack>
        ))}
      </BlockStack>
    </Card>
  );
}

export default FieldCaptureCard;
